import { useEffect, useState } from "react";
import { UserPlus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { ROLE_LABEL } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ROLE_BADGE = {
  admin: "bg-violet-50 text-violet-700 border-violet-200",
  responsabile: "bg-blue-50 text-blue-700 border-blue-200",
  tecnico: "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const empty = { name: "", email: "", password: "", role: "tecnico", phone: "", hourly_cost: 0, hourly_cost_extra: 0, magazzino_warehouse_id: "", magazzino_warehouse_name: "", fuel_card_ids: [] };

export default function Utenti() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [fuelCards, setFuelCards] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const isAdmin = user?.role === "admin";

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
    api.get("/magazzino/warehouses").then((r) => setWarehouses(r.data)).catch(() => setWarehouses([]));
    api.get("/fuel-cards").then((r) => setFuelCards(r.data)).catch(() => setFuelCards([]));
  }, []);

  const openNew = () => { setForm(empty); setEditing(null); setDlg(true); };
  const openEdit = (u) => { setForm({ ...u, password: "", fuel_card_ids: u.fuel_card_ids || [] }); setEditing(u); setDlg(true); };

  const save = async () => {
    if (!form.name || !form.email) { toast.error("Nome ed email obbligatori"); return; }
    if (!editing && !form.password) { toast.error("Password obbligatoria"); return; }
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.put(`/users/${editing.id}`, payload);
      } else {
        await api.post("/users", form);
      }
      toast.success("Salvato"); setDlg(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare l'utente?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <PageHeader overline="Flux · Configurazione" title="Utenti & ruoli" subtitle="Gestisci gli account e i ruoli di accesso">
        <Button onClick={openNew} data-testid="new-user-btn"><UserPlus size={18} />Nuovo utente</Button>
      </PageHeader>

      <div className="space-y-3">
        {users.map((u) => (
          <div key={u.id} className="flex items-center justify-between rounded-xl border border-border bg-card px-5 py-4" data-testid={`user-row-${u.email}`}>
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-navy text-sm font-bold text-white">{u.name?.[0]?.toUpperCase()}</span>
              <div>
                <p className="font-bold text-navy">{u.name}</p>
                <p className="text-sm text-muted-foreground">{u.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {u.role === "tecnico" && u.magazzino_warehouse_name && (
                <span className="hidden rounded-full border border-border bg-secondary/50 px-2.5 py-0.5 text-xs font-semibold text-navy sm:inline">🚚 {u.magazzino_warehouse_name}</span>
              )}
              <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${ROLE_BADGE[u.role]}`}>{ROLE_LABEL[u.role]}</span>
              <button onClick={() => openEdit(u)} className="rounded p-1.5 text-muted-foreground hover:bg-secondary hover:text-primary"><Pencil size={16} /></button>
              {isAdmin && u.id !== user.id && (
                <button onClick={() => remove(u.id)} data-testid={`delete-user-${u.email}`} className="rounded p-1.5 text-muted-foreground hover:bg-red-50 hover:text-destructive"><Trash2 size={16} /></button>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifica utente" : "Nuovo utente"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="space-y-1.5"><Label>Nome *</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Email *</Label><Input type="email" data-testid="user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Password {editing && <span className="text-muted-foreground">(opz.)</span>}</Label><Input type="password" data-testid="user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Ruolo</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="responsabile">Responsabile</SelectItem>
                    <SelectItem value="tecnico">Tecnico</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Telefono</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            {form.role === "tecnico" && (
              <div className="space-y-1.5">
                <Label>Costo orario ordinario (€/h)</Label>
                <Input type="number" min="0" step="0.5" data-testid="user-hourly-cost" value={form.hourly_cost ?? 0} onChange={(e) => setForm({ ...form, hourly_cost: parseFloat(e.target.value) || 0 })} />
                <p className="text-xs text-muted-foreground">Feriali. Usato nella pagina Risultati (con le presenze).</p>
              </div>
            )}
            {form.role === "tecnico" && (
              <div className="space-y-1.5">
                <Label>Costo orario straordinario (€/h)</Label>
                <Input type="number" min="0" step="0.5" data-testid="user-hourly-cost-extra" value={form.hourly_cost_extra ?? 0} onChange={(e) => setForm({ ...form, hourly_cost_extra: parseFloat(e.target.value) || 0 })} />
                <p className="text-xs text-muted-foreground">Sabato e domenica.</p>
              </div>
            )}
            {form.role === "tecnico" && (
              <div className="space-y-1.5">
                <Label>Magazzino mobile</Label>
                <Select
                  value={form.magazzino_warehouse_id || "none"}
                  onValueChange={(v) => {
                    const w = warehouses.find((x) => x.id === v);
                    setForm({ ...form, magazzino_warehouse_id: v === "none" ? "" : v, magazzino_warehouse_name: w ? w.name : "" });
                  }}
                >
                  <SelectTrigger data-testid="user-warehouse"><SelectValue placeholder="Nessun magazzino" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessun magazzino</SelectItem>
                    {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Collega il tecnico al suo magazzino mobile nell'app magazzino (per prodotti e seriali).</p>
              </div>
            )}
            {form.role === "tecnico" && (
              <div className="space-y-1.5">
                <Label>Carta carburante assegnata</Label>
                <Select
                  value={(form.fuel_card_ids && form.fuel_card_ids[0]) || "none"}
                  onValueChange={(v) => setForm({ ...form, fuel_card_ids: v === "none" ? [] : [v] })}
                >
                  <SelectTrigger data-testid="user-fuel-card"><SelectValue placeholder="Nessuna carta" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuna carta</SelectItem>
                    {fuelCards.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`user-fuelcard-${c.id}`}>{c.label || c.number}</SelectItem>)}
                  </SelectContent>
                </Select>
                {fuelCards.length === 0 && <p className="text-xs text-muted-foreground">Nessuna carta disponibile. Creale in Carburante → Carte carburante.</p>}
                <p className="text-xs text-muted-foreground">Il tecnico potrà inviare richieste di rifornimento solo con una carta assegnata.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>Annulla</Button>
            <Button onClick={save} data-testid="user-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
